import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface NavItem {
  id: string;
  name: string;
  iconPath: string;
  iconClickedPath: string;
  path: string;
}

type Platform = 'mobile' | 'desktop';
type UserRole = 'buyer' | 'seller' | 'admin';

interface BottomNavigationProps {
  activeId: string;
  platform?: Platform;
  userRole?: UserRole;
  className?: string;
}

export function BottomNavigation({ 
  activeId, 
  platform = 'mobile', 
  userRole, // userRole prop은 선택적으로 변경
  className = ''
}: BottomNavigationProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // 실제 사용자 역할 결정 (props > user.role > 기본값)
  const actualUserRole: UserRole = userRole || (user?.role as UserRole) || 'buyer';
  const getNavItems = (): NavItem[] => {
    const buyerItems: NavItem[] = [
      {
        id: 'home',
        name: '홈',
        iconPath: '/icons/home_button.svg',
        iconClickedPath: '/icons/home_button_clicked.svg',
        path: '/'
      },
      {
        id: 'favorites',
        name: '단골가게',
        iconPath: '/icons/favorites_button.svg',
        iconClickedPath: '/icons/favorites_button_clicked.svg',
        path: '/favorites'
      },
      {
        id: 'orders',
        name: '주문내역',
        iconPath: '/icons/orders_button.svg',
        iconClickedPath: '/icons/orders_button_clicked.svg',
        path: '/buyer/orders'
      },
      // 로그인 상태에 따라 마지막 탭 변경
      user ? {
        id: 'mypage',
        name: '마이페이지',
        iconPath: '/icons/my_page_button.svg',
        iconClickedPath: '/icons/my_page_button_clicked.svg',
        path: '/mypage'
      } : {
        id: 'login',
        name: '로그인',
        iconPath: '/icons/icon_login.svg',
        iconClickedPath: '/icons/icon_login.svg',
        path: '/login'
      }
    ];

    const sellerItems: NavItem[] = [
      {
        id: 'home',
        name: '홈',
        iconPath: '/icons/home_button.svg',
        iconClickedPath: '/icons/home_button_clicked.svg',
        path: '/seller/home'
      },
      {
        id: 'store',
        name: '내 가게',
        iconPath: '/icons/shop_button.svg',
        iconClickedPath: '/icons/shop_button.svg',
        path: '/seller/store'
      },
      {
        id: 'mypage',
        name: '마이페이지',
        iconPath: '/icons/my_page_button.svg',
        iconClickedPath: '/icons/my_page_button_clicked.svg',
        path: '/seller/mypage'
      }
    ];

    const adminItems: NavItem[] = [
      {
        id: 'overview',
        name: '개요',
        iconPath: '/icons/admin_overview.svg',
        iconClickedPath: '/icons/admin_overview_clicked.svg',
        path: '/admin/overview'
      },
      {
        id: 'users',
        name: '사용자관리',
        iconPath: '/icons/admin_users.svg',
        iconClickedPath: '/icons/admin_users_clicked.svg',
        path: '/admin/users'
      },
      {
        id: 'restaurants',
        name: '매장관리',
        iconPath: '/icons/admin_restaurants.svg',
        iconClickedPath: '/icons/admin_restaurants_clicked.svg',
        path: '/admin/restaurants'
      },
      {
        id: 'analytics',
        name: '분석',
        iconPath: '/icons/admin_analytics.svg',
        iconClickedPath: '/icons/admin_analytics_clicked.svg',
        path: '/admin/analytics'
      }
    ];

    switch (actualUserRole) {
      case 'seller':
        return sellerItems;
      case 'admin':
        return adminItems;
      default:
        return buyerItems;
    }
  };

  const navItems = getNavItems();
  
  // 현재 경로에 따른 activeId 결정
  const currentActiveId = (() => {
    const path = location.pathname;
    
    // 구매자 경로
    if (actualUserRole === 'buyer') {
      if (path === '/') return 'home';
      if (path === '/favorites') return 'favorites';
      if (path === '/buyer/orders' || path === '/orders') return 'orders';
      if (path === '/login') return 'login';
      if (path === '/mypage') return 'mypage';
    }
    
    // 판매자 경로
    if (actualUserRole === 'seller') {
      if (path === '/seller/home') return 'home';
      if (path === '/seller/store') return 'store';
      if (path === '/seller/mypage' || path.startsWith('/seller/mypage/')) return 'mypage';
    }
    
    return activeId; // 기본값 사용
  })();
  
  const handleNavClick = (item: NavItem) => {
    // 구매자 네비게이션
    if (actualUserRole === 'buyer') {
      if (item.path === '/' || item.path === '/favorites' || item.path === '/login' || item.path === '/mypage') {
        navigate(item.path);
      } else if (item.path === '/buyer/orders') {
        // 주문내역은 로그인이 필요한 페이지
        if (user) {
          navigate('/buyer/orders');
        } else {
          alert('로그인이 필요한 서비스입니다.');
          navigate('/login');
        }
      } else {
        // 아직 구현되지 않은 페이지
        alert(`${item.name} 페이지는 추후 구현 예정입니다.`);
      }
    }
    
    // 판매자 네비게이션
    if (actualUserRole === 'seller') {
      if (item.path === '/seller/home') {
        navigate('/seller/home');
      } else if (item.path === '/seller/store') {
        navigate('/seller/store');
      } else if (item.path === '/seller/mypage') {
        navigate('/seller/mypage');
      } else {
        // 기타 페이지
        alert(`${item.name} 페이지는 추후 구현 예정입니다.`);
      }
    }
  };

  const platformStyles = {
    mobile: {
      container: { 
        backgroundColor: 'white', 
        borderTop: '1px solid #DEDEDE', 
        width: '100%',
        padding: '12px 0',
        position: 'fixed' as const,
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 50
      },
      iconSize: '56px'
    },
    desktop: {
      container: { 
        backgroundColor: 'white', 
        borderRight: '1px solid #DEDEDE', 
        height: '100vh',
        width: '240px',
        padding: '24px 16px',
        position: 'fixed' as const,
        left: 0,
        top: 0
      },
      iconSize: '32px'
    }
  };

  const currentStyles = platformStyles[platform];

  return (
    <div 
      style={currentStyles.container}
      className={className}
    >
      <div style={{ 
        display: 'flex', 
        flexDirection: platform === 'desktop' ? 'column' : 'row',
        alignItems: 'center', 
        width: '100%',
        gap: platform === 'desktop' ? '16px' : '0'
      }}>
        {navItems.map((item) => (
          <div
            key={item.id}
            style={{
              display: 'flex',
              flexDirection: platform === 'desktop' ? 'row' : 'column',
              alignItems: 'center',
              justifyContent: platform === 'desktop' ? 'flex-start' : 'center',
              flex: platform === 'mobile' ? 1 : 'none',
              width: platform === 'desktop' ? '100%' : 'auto',
              cursor: 'pointer',
              transition: 'background-color 0.15s',
              padding: platform === 'desktop' ? '12px 16px' : '0',
              borderRadius: platform === 'desktop' ? '8px' : '0',
              gap: platform === 'desktop' ? '12px' : '0'
            }}
            onClick={() => handleNavClick(item)}
            onMouseDown={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseUp={(e) => e.currentTarget.style.backgroundColor = item.id === currentActiveId && platform === 'desktop' ? '#e5f3ff' : 'transparent'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = item.id === currentActiveId && platform === 'desktop' ? '#e5f3ff' : 'transparent'}
          >
            <img
              src={item.id === currentActiveId ? item.iconClickedPath : item.iconPath}
              alt={item.name}
              style={{ 
                width: item.id === 'store' ? (platform === 'mobile' ? '64px' : '38px') : currentStyles.iconSize,
                height: item.id === 'store' ? (platform === 'mobile' ? '64px' : '38px') : currentStyles.iconSize,
                // 선택된 아이콘에 대해 통일된 초록색 필터 적용
                filter: item.id === currentActiveId 
                  ? 'invert(48%) sepia(79%) saturate(2476%) hue-rotate(86deg) brightness(118%) contrast(119%)'
                  : 'none'
              }}
            />
            {platform === 'desktop' && (
              <span 
                style={{
                  fontSize: '16px',
                  fontWeight: item.id === currentActiveId ? 'bold' : 'normal',
                  color: item.id === currentActiveId ? '#007AFF' : '#333333'
                }}
              >
                {item.name}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}